import React, { useEffect, useRef } from 'react';
import { Shield, ChevronRight, Terminal } from 'lucide-react';
import { motion } from 'framer-motion';
export function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationFrameId: number;
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    const chars = '01'.split('');
    const fontSize = 14;
    let columns = canvas.width / fontSize;
    let drops: number[] = [];
    // Initialize drops
    for (let x = 0; x < columns; x++) {
      drops[x] = Math.random() * -100; // Start off-screen randomly
    }
    const draw = () => {
      // Re-calculate columns in case of resize
      columns = canvas.width / fontSize;
      while (drops.length < columns) drops.push(Math.random() * -100);
      // Translucent dark background to create trail effect
      ctx.fillStyle = 'rgba(10, 14, 23, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${fontSize}px monospace`;
      for (let i = 0; i < drops.length; i++) {
        // Randomize color slightly between steel blue and cyan
        const isCyan = Math.random() > 0.9;
        ctx.fillStyle = isCyan ? '#06b6d4' : '#4A90D9';
        // Make characters slightly darker/transparent randomly
        ctx.globalAlpha = Math.random() * 0.5 + 0.2;
        const text = chars[Math.floor(Math.random() * chars.length)];
        // Draw character
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);
        // Reset drop to top randomly after it crosses screen
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }
        drops[i]++;
      }
      ctx.globalAlpha = 1.0;
      // Control speed
      setTimeout(() => {
        animationFrameId = requestAnimationFrame(draw);
      }, 50);
    };
    draw();
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);
  return <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden" data-id="element-102">
      {/* Binary Rain Canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 z-0 opacity-40" style={{
      pointerEvents: 'none'
    }} data-id="element-103" />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-brand-dark/50 via-brand-dark/80 to-brand-dark" data-id="element-104" />
      <div className="absolute inset-0 z-0 bg-hero-glow" data-id="element-105" />

      <div className="container mx-auto px-6 relative z-10" data-id="element-106">
        <div className="max-w-4xl mx-auto text-center" data-id="element-107">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-darker border border-brand-border mb-8" data-id="element-108">
            <Terminal className="w-4 h-4 text-brand-cyan" data-id="element-109" />
            <span className="text-xs font-mono text-brand-cyan uppercase tracking-wider" data-id="element-110">
              System Status: Compromised
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-5xl md:text-7xl font-mono font-bold text-white leading-tight mb-6" data-id="element-111">
            Your Perimeter Is <br className="hidden md:block" data-id="element-112" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-steel to-brand-cyan" data-id="element-113">
              Already Breached.
            </span>
            <br data-id="element-114" /> We Seal It.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-lg md:text-xl text-slate-400 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-115">
            Military-grade proactive cybersecurity for enterprises. We deploy
            zero-trust architecture and AI-driven threat intelligence to
            neutralize incursions before they execute.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-116">
            <a href="#contact" className="w-full sm:w-auto px-8 py-4 bg-brand-steel hover:bg-brand-steel-light text-white font-mono font-medium rounded-md transition-all flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(74,144,217,0.4)] hover:shadow-[0_0_30px_rgba(74,144,217,0.6)]" data-id="element-117">
              <Shield className="w-5 h-5" data-id="element-118" />
              Schedule Threat Assessment
            </a>
            <a href="#solutions" className="w-full sm:w-auto px-8 py-4 bg-transparent border border-brand-border hover:border-brand-steel text-slate-300 hover:text-white font-mono font-medium rounded-md transition-all flex items-center justify-center gap-2 group" data-id="element-119">
              View Solutions
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-120" />
            </a>
          </motion.div>
        </div>
      </div>

      {/* Decorative bottom border */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-steel/50 to-transparent" data-id="element-121" />
    </section>;
}